import React from 'react'
import styled from '@emotion/styled'
import { BreakPoint, Styles } from 'src/components/shared/CssValues'
import { Typography } from 'src/components/atoms/Typography'

const FEATURE_PC_HEIGHT = '56px'
const FEATURE_SP_HEIGHT = '44px'

type PlanProps = {
  tableHead: boolean
}

type PlanInnerProps = {
  highlighted: boolean
}

type PlanNameProps = {
  highlighted: boolean
  tableHead: boolean
}

type FeatureGroupProps = {
  first: boolean
}

type FeatureProps = {
  hideIfPc?: boolean
}

type FeatureAvailabilityProps = {
  tableHead: boolean
}

type TFeature = {
  featureName: string
  availability: boolean
}

type TFeatureGroup = {
  featureGroupName: string
  features: TFeature[]
}

type TPlans = {
  planName: string
  featureGroups: TFeatureGroup[]
}

export const planNames = {
  light: 'ライト',
  standard: 'スタンダード',
  professional: 'プロフェッショナル',
  enterprise: 'エンタープライズ'
}

const featureGroups = {
  customerSuccessTeamSupport: 'カスタマーサクセスによる支援',
  communityFunctions: 'コミュニティ機能',
  security: 'セキュリティ・計測・拡張'
}

export const features = {
  launchSupport: 'サイト立ち上げ支援',
  chatSupport: 'チャットサポート/管理保守',
  monthlyReport: '月次レポート/ディスカッション',
  eventAndSeminar: 'コミュニティ/イベント・セミナー',
  userSurvey: 'ユーザーサーベイ / アクション促進',
  webhook: 'Webhookでの投稿情報の連携',
  notification: 'お知らせ/通知',
  mobileApp: 'モバイルアプリ',
  analysis:'分析ダッシュボード',
  group: 'グループ',
  customerService: 'ウェブ接客',
  pointAndBadge: 'ポイント/バッジ',
  dm: 'ダイレクトメール',
  qa: 'Q&A',
  knowledgeBase: 'ナレッジベース',
  monitoring: '24時間365日の投稿モニタリング',
  api: 'commmune API',
  sso: 'SSO',
  ga: 'Google Analytics(GA)の埋め込み',
  gtm: 'GA/GTMによる各種タグ埋め込み',
  training: 'トレーニング',
  originalDomain: '独自ドメイン',
  customizeReport: 'カスタマイズレポート',
  originalApp: '独自アプリ',
  moreSupport: 'より手厚いサポート'
}

const plans: TPlans[] = [
  {
    planName: planNames.light,
    featureGroups: [
      {
        featureGroupName: featureGroups.customerSuccessTeamSupport,
        features: [
          {
            featureName: features.launchSupport,
            availability: true
          },
          {
            featureName: features.chatSupport,
            availability: true
          },
          {
            featureName: features.monthlyReport,
            availability: true
          },
          {
            featureName: features.eventAndSeminar,
            availability: true
          },
          {
            featureName: features.userSurvey,
            availability: true
          }
        ]
      },
      {
        featureGroupName: featureGroups.communityFunctions,
        features: [
          {
            featureName: features.group,
            availability: true
          },
          {
            featureName: features.customerService,
            availability: true
          },
          {
            featureName: features.pointAndBadge,
            availability: true
          },
          {
            featureName: features.notification,
            availability: true
          },
          {
            featureName: features.mobileApp,
            availability: true
          },
          {
            featureName: features.eventAndSeminar,
            availability: true
          },
          {
            featureName: features.webhook,
            availability: true
          },
          {
            featureName: features.dm,
            availability: false
          },
          {
            featureName: features.qa,
            availability: false
          },
          {
            featureName: features.knowledgeBase,
            availability: false
          },
          {
            featureName: features.training,
            availability: false
          }
        ]
      },
      {
        featureGroupName: featureGroups.security,
        features: [
          {
            featureName: features.analysis,
            availability: true
          },
          {
            featureName: features.monitoring,
            availability: false
          },
          {
            featureName: features.api,
            availability: false
          },
          {
            featureName: features.sso,
            availability: false
          },
          {
            featureName: features.ga,
            availability: false
          },
          {
            featureName: features.gtm,
            availability: false
          },
          {
            featureName: features.originalDomain,
            availability: false
          },
          {
            featureName: features.customizeReport,
            availability: false
          },
          {
            featureName: features.originalApp,
            availability: false
          }
        ]
      }
    ]
  },
  {
    planName: planNames.standard,
    featureGroups: [
      {
        featureGroupName: featureGroups.customerSuccessTeamSupport,
        features: [
          {
            featureName: features.launchSupport,
            availability: true
          },
          {
            featureName: features.chatSupport,
            availability: true
          },
          {
            featureName: features.monthlyReport,
            availability: true
          },
          {
            featureName: features.eventAndSeminar,
            availability: true
          },
          {
            featureName: features.userSurvey,
            availability: true
          }
        ]
      },
      {
        featureGroupName: featureGroups.communityFunctions,
        features: [
          {
            featureName: features.group,
            availability: true
          },
          {
            featureName: features.customerService,
            availability: true
          },
          {
            featureName: features.pointAndBadge,
            availability: true
          },
          {
            featureName: features.notification,
            availability: true
          },
          {
            featureName: features.mobileApp,
            availability: true
          },
          {
            featureName: features.eventAndSeminar,
            availability: true
          },
          {
            featureName: features.webhook,
            availability: true
          },
          {
            featureName: features.dm,
            availability: true
          },
          {
            featureName: features.qa,
            availability: true
          },
          {
            featureName: features.knowledgeBase,
            availability: true
          },
          {
            featureName: features.training,
            availability: true
          }
        ]
      },
      {
        featureGroupName: featureGroups.security,
        features: [
          {
            featureName: features.analysis,
            availability: true
          },
          {
            featureName: features.monitoring,
            availability: true
          },
          {
            featureName: features.api,
            availability: true
          },
          {
            featureName: features.sso,
            availability: true
          },
          {
            featureName: features.ga,
            availability: true
          },
          {
            featureName: features.gtm,
            availability: true
          },
          {
            featureName: features.originalDomain,
            availability: false
          },
          {
            featureName: features.customizeReport,
            availability: false
          },
          {
            featureName: features.originalApp,
            availability: false
          }
        ]
      }
    ]
  },
  {
    planName: planNames.professional,
    featureGroups: [
      {
        featureGroupName: featureGroups.customerSuccessTeamSupport,
        features: [
          {
            featureName: features.launchSupport,
            availability: true
          },
          {
            featureName: features.chatSupport,
            availability: true
          },
          {
            featureName: features.monthlyReport,
            availability: true
          },
          {
            featureName: features.eventAndSeminar,
            availability: true
          },
          {
            featureName: features.userSurvey,
            availability: true
          }
        ]
      },
      {
        featureGroupName: featureGroups.communityFunctions,
        features: [
          {
            featureName: features.group,
            availability: true
          },
          {
            featureName: features.customerService,
            availability: true
          },
          {
            featureName: features.pointAndBadge,
            availability: true
          },
          {
            featureName: features.notification,
            availability: true
          },
          {
            featureName: features.mobileApp,
            availability: true
          },
          {
            featureName: features.eventAndSeminar,
            availability: true
          },
          {
            featureName: features.webhook,
            availability: true
          },
          {
            featureName: features.dm,
            availability: true
          },
          {
            featureName: features.qa,
            availability: true
          },
          {
            featureName: features.knowledgeBase,
            availability: true
          },
          {
            featureName: features.training,
            availability: true
          }
        ]
      },
      {
        featureGroupName: featureGroups.security,
        features: [
          {
            featureName: features.analysis,
            availability: true
          },
          {
            featureName: features.monitoring,
            availability: true
          },
          {
            featureName: features.api,
            availability: true
          },
          {
            featureName: features.sso,
            availability: true
          },
          {
            featureName: features.ga,
            availability: true
          },
          {
            featureName: features.gtm,
            availability: true
          },
          {
            featureName: features.originalDomain,
            availability: true
          },
          {
            featureName: features.customizeReport,
            availability: true
          },
          {
            featureName: features.originalApp,
            availability: true
          }
        ]
      }
    ]
  },
]

const IconCheckMark: React.VFC = () => (
  <svg
    width="14"
    height="12"
    viewBox="0 0 14 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>提供</title>
    <path d="M1 6.0625L4.65217 10L13 1" stroke="black" strokeWidth="1.5" />
  </svg>
)

export const PlanComparison = () => {
  return (
    <Container>
      {plans.map((plan, i) => {
        const tableHead = i === 0
        const highlighted = plan.planName === planNames.standard
        return (
          <Plan key={plan.planName} tableHead={tableHead}>
            <PlanInner highlighted={highlighted}>
              <PlanName highlighted={highlighted} tableHead={tableHead}>
                <PlanNameText>{plan.planName}</PlanNameText>
              </PlanName>
              {plan.featureGroups.map((featureGroup, i) => (
                <FeatureGroup
                  key={featureGroup.featureGroupName}
                  first={i === 0}
                >
                  <FeatureGroupName>
                    <FeatureGroupNameText hideIfPc={!tableHead}>
                      {featureGroup.featureGroupName}
                    </FeatureGroupNameText>
                  </FeatureGroupName>
                  {featureGroup.features.map(feature => (
                    <FeatureItem key={feature.featureName}>
                      <FeatureName hideIfPc={!tableHead}>
                        {feature.featureName}
                      </FeatureName>
                      <FeatureAvailability tableHead={tableHead}>
                        {feature.availability ? (
                          <IconCheckMark />
                        ) : (
                          <FeatureAvailabilityUnavailable />
                        )}
                      </FeatureAvailability>
                    </FeatureItem>
                  ))}
                </FeatureGroup>
              ))}
            </PlanInner>
          </Plan>
        )
      })}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  margin: 0 auto;
  &::-webkit-scrollbar {
    display: none;
  }
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    max-width: 990px;
    padding: 0 40px;
  }
  @media screen and (min-width: ${BreakPoint.LAPTOP}) {
    padding: 0 50px;
  }
`

const Plan = styled.div<PlanProps>`
  scroll-snap-align: start;
  box-sizing: border-box;
  padding: 0 0 0 20px;
  @media screen and (min-width: ${BreakPoint.MOBILE}) {
    padding: 0 0 0 30px;
  }
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    padding: 0;
    width: ${({ tableHead }) => (tableHead ? '49%' : '17%')};
  }
  &:last-child {
    padding: 0 20px;
    @media screen and (min-width: ${BreakPoint.MOBILE}) {
      padding: 0 30px;
    }
    @media screen and (min-width: ${BreakPoint.TABLET}) {
      padding: 0;
    }
  }
`

const PlanInner = styled.div<PlanInnerProps>`
  border: 1px solid ${Styles.color.primary[400]};
  border-top-width: 11px;
  padding: 20px;
  background: ${({ highlighted }) =>
    highlighted ? Styles.color.primary[200] : 'none'};
  @media screen and (min-width: ${BreakPoint.MOBILE}) {
    padding: 30px;
  }
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    padding: 0;
    border: none;
    position: relative;
  }
`

const PlanName = styled.div<PlanNameProps>`
  margin: ${Styles.spacing[9]} 0 0;
  height: 65px;
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    margin: 0;
    position: absolute;
    top: 0;
    right: 0;
    height: ${FEATURE_PC_HEIGHT};
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ tableHead }) => (tableHead ? '34.693877%' : '100%')};
    padding: ${({ highlighted }) =>
      highlighted ? `0 ${Styles.spacing[5]}` : '0'};
    box-sizing: border-box;
  }
`

const PlanNameText = styled(Typography)`
  text-align: center;
  font-weight: ${Styles.fontWeight.bold};
  line-height: ${Styles.lineHeight[200]};
  letter-spacing: ${Styles.letterSpacing[400]};
  color: ${Styles.color.primary[400]};
  font-size: ${Styles.fontSize[600]};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    font-size: ${Styles.fontSize[300]};
  }
`

const FeatureGroup = styled.div<FeatureGroupProps>`
  margin: ${({ first }) => (first ? '0' : `${Styles.spacing[11]} 0 0`)};
  width: calc(100vw - 20px * 6);
  @media screen and (min-width: ${BreakPoint.MOBILE}) {
    width: calc(100vw - 30px * 6);
  }
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    width: auto;
    margin: ${({ first }) => (first ? '0' : `${Styles.spacing[10]} 0 0`)};
  }
`

const FeatureGroupName = styled.div`
  margin: 0 0 ${Styles.spacing[7]};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    margin: 0;
    display: flex;
    align-items: center;
    height: ${FEATURE_PC_HEIGHT};
  }
`

const FeatureGroupNameText = styled(Typography)<FeatureProps>`
  line-height: ${Styles.lineHeight[200]};
  letter-spacing: ${Styles.letterSpacing[400]};
  font-weight: ${Styles.fontWeight.bold};
  color: ${Styles.color.primary[400]};
  font-size: ${Styles.fontSize[400]};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    display: ${({ hideIfPc }) => (hideIfPc ? 'none' : 'block')};
  }
`

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid ${Styles.color.border[400]};
  height: ${FEATURE_SP_HEIGHT};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    height: ${FEATURE_PC_HEIGHT};
  }
`

const FeatureName = styled(Typography)<FeatureProps>`
  flex: 1;
  min-width: 0;
  font-size: ${Styles.fontSize[300]};
  line-height: ${Styles.lineHeight[200]};
  letter-spacing: ${Styles.letterSpacing[400]};
  margin: 0 ${Styles.spacing[8]} 0 0;
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    display: ${({ hideIfPc }) => (hideIfPc ? 'none' : 'block')};
  }
`

const FeatureAvailability = styled.div<FeatureAvailabilityProps>`
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    width: ${({ tableHead }) => (tableHead ? '34.693877%' : '100%')};
    text-align: center;
  }
`

const FeatureAvailabilityUnavailable = styled.div`
  width: 14px;
  height: 12px;
  display: flex;
  align-items: center;
  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 0;
    border-top: 1px solid #ccc;
  }
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    display: none;
  }
`
